import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarPlus, FileArrowDown, Paperclip, Plus, Wrench } from '@phosphor-icons/react';
import { API } from '../lib/api';
import { useSEO } from '../lib/seo';
import { useAuthUser } from '../lib/useAuth';
import { SERVICES } from '../data/site';
import { DashboardShell, EmptyState, Panel, StatCard, StatusPill, formatDate, inputCls } from '../components/dashboard/Bits';

const emptyItem = {
  item_name: '', category: 'Calibration services', site_name: '', country: 'SG',
  contractor_name: '', contractor_email: '', contractor_phone: '', last_service_date: '', next_due_date: '', notes: '', status: 'active',
};
const emptyVisit = { visit_date: '', contractor_name: '', work_done: '', next_due_date: '', notes: '' };

const daysUntil = (dateText) => {
  if (!dateText) return null;
  const due = new Date(`${dateText}T00:00:00Z`);
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((due.getTime() - today) / 86400000);
};

const errorText = (err, fallback) => {
  const detail = err.response?.data?.detail;
  if (Array.isArray(detail)) return detail[0]?.msg || fallback;
  if (detail && typeof detail === 'object') return detail.msg || fallback;
  return detail || fallback;
};

const ServiceRegister = () => {
  useSEO({ title: 'Vox Service Manager — Service Records & Renewals', description: '', path: '/dashboard/services', robots: 'noindex, nofollow' });
  const auth = useAuthUser();
  const [access, setAccess] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [visitForm, setVisitForm] = useState(emptyVisit);
  const [visitFile, setVisitFile] = useState(null);
  const [requestForm, setRequestForm] = useState({ company_name: '', notes: '' });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const enabled = !!access?.customer && ['pilot', 'active'].includes(access.customer.plan_status);
  const selected = useMemo(() => items.find((i) => i.id === selectedId) || null, [items, selectedId]);

  const loadAccess = useCallback(async () => {
    if (auth.status !== 'signed-in') return;
    const { data } = await axios.get(`${API}/service-register/access`, { withCredentials: true });
    setAccess(data);
    return data;
  }, [auth.status]);

  const loadItems = useCallback(async () => {
    if (auth.status !== 'signed-in') return;
    setLoading(true);
    try {
      const data = await loadAccess();
      if (data?.customer && ['pilot', 'active'].includes(data.customer.plan_status)) {
        const res = await axios.get(`${API}/service-register/items`, { withCredentials: true });
        setItems(res.data.items || []);
        setSelectedId((current) => current || res.data.items?.[0]?.id || null);
      }
    } catch (err) {
      toast.error(errorText(err, 'Could not load Vox Service Manager.'));
    } finally {
      setLoading(false);
    }
  }, [auth.status, loadAccess]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const requestAccess = async (e) => {
    e.preventDefault();
    setActing(true);
    try {
      await axios.post(`${API}/service-register/request-access`, requestForm, { withCredentials: true });
      toast.success('Pilot request sent to the founder.');
      await loadItems();
    } catch (err) {
      toast.error(errorText(err, 'Request failed.'));
    } finally {
      setActing(false);
    }
  };

  const createItem = async (e) => {
    e.preventDefault();
    setActing(true);
    try {
      const { data } = await axios.post(`${API}/service-register/items`, itemForm, { withCredentials: true });
      toast.success('Service item added.');
      setItemForm(emptyItem);
      await loadItems();
      setSelectedId(data.item.id);
    } catch (err) {
      toast.error(errorText(err, 'Could not add item.'));
    } finally {
      setActing(false);
    }
  };

  const createVisit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setActing(true);
    try {
      const fd = new FormData();
      Object.entries(visitForm).forEach(([k, v]) => fd.append(k, v));
      if (visitFile) fd.append('file', visitFile);
      await axios.post(`${API}/service-register/items/${selected.id}/visits`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Service visit recorded.');
      setVisitForm(emptyVisit);
      setVisitFile(null);
      await loadItems();
    } catch (err) {
      toast.error(errorText(err, 'Could not record visit.'));
    } finally {
      setActing(false);
    }
  };

  const requestVisit = async () => {
    if (!selected) return;
    setActing(true);
    try {
      const { data } = await axios.post(`${API}/service-register/items/${selected.id}/request-visit`, {}, { withCredentials: true });
      toast.success(`RFQ created. ${data.matched_providers} provider${data.matched_providers === 1 ? '' : 's'} matched.`);
    } catch (err) {
      toast.error(errorText(err, 'Could not create RFQ.'));
    } finally {
      setActing(false);
    }
  };

  if (auth.status === 'checking' || (auth.status === 'signed-in' && loading && !access)) {
    return <div className="flex min-h-[70vh] items-center justify-center bg-ink" data-testid="service-register-loading"><p className="mono-label text-steel">Loading Vox Service Manager…</p></div>;
  }
  if (auth.status === 'signed-out') {
    return <DashboardShell eyebrow="Vox Service Manager" title={<>Sign in first.</>} sub="Service records and renewal reminders are private to your company account."><EmptyState testId="service-register-signed-out" title="Google sign-in required." text="Sign in with the company email enabled for the Vox Service Manager pilot." actionLabel="Go to sign in" actionTo="/account" /></DashboardShell>;
  }

  if (!enabled) {
    const pending = !!access?.pending_request;
    return (
      <DashboardShell eyebrow="Vox Service Manager" title={<>Register free.</>} sub="Free during the pilot and available worldwide. Vox reviews each registration personally before enabling the workspace.">
        <div className="mx-auto max-w-2xl">
          <Panel testId="service-register-request-panel" title={pending ? 'Registration pending.' : 'Register for the free pilot.'}>
            {pending ? (
              <p className="text-sm leading-relaxed text-steel" data-testid="service-register-pending">Your request is with the founder. You will receive an email when the register is enabled.</p>
            ) : (
              <form onSubmit={requestAccess} className="space-y-5" data-testid="service-register-request-form">
                <div><label className="mono-label mb-2 block text-steel">Company name</label><input required className={inputCls} value={requestForm.company_name} onChange={(e) => setRequestForm({ ...requestForm, company_name: e.target.value })} data-testid="service-request-company-input" /></div>
                <div><label className="mono-label mb-2 block text-steel">What do you want to track?</label><textarea rows={4} className={`${inputCls} resize-y`} value={requestForm.notes} onChange={(e) => setRequestForm({ ...requestForm, notes: e.target.value })} data-testid="service-request-notes-textarea" placeholder="Calibrated instruments, air-conditioning, pest control, IT support…" /></div>
                <button type="submit" disabled={acting} data-testid="service-request-submit-button" className="w-full bg-volt px-5 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink disabled:opacity-50">{acting ? 'Sending…' : 'Register free'}</button>
              </form>
            )}
          </Panel>
        </div>
      </DashboardShell>
    );
  }

  const dueSoon = items.filter((i) => { const d = daysUntil(i.next_due_date); return d !== null && d <= 30 && d >= 0; }).length;
  const overdue = items.filter((i) => (daysUntil(i.next_due_date) ?? 1) < 0).length;
  const visits = items.reduce((sum, i) => sum + (i.visits?.length || 0), 0);

  return (
    <DashboardShell
      eyebrow="Vox Service Manager"
      title={<>Records. Renewals. Reports.</>}
      sub={`${access.customer.company_name} · calibration and maintenance records with 30/14/7/due reminders.`}
      actions={<Link to="/dashboard/buyer" data-testid="service-register-open-buyer" className="border border-line px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-parchment hover:border-volt hover:text-volt">Open buyer dashboard</Link>}
    >
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="service-register-stats">
        <StatCard label="Tracked items" value={items.length} testId="service-stat-items" />
        <StatCard label="Due in 30 days" value={dueSoon} testId="service-stat-due-soon" />
        <StatCard label="Overdue" value={overdue} testId="service-stat-overdue" />
        <StatCard label="Visits recorded" value={visits} testId="service-stat-visits" />
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        <Panel title="Add service item" testId="service-item-add-panel" className="lg:col-span-5">
          <form onSubmit={createItem} className="space-y-4" data-testid="service-item-form">
            <div><label className="mono-label mb-2 block text-steel">Item / service</label><input required className={inputCls} value={itemForm.item_name} onChange={(e) => setItemForm({ ...itemForm, item_name: e.target.value })} data-testid="service-item-name-input" placeholder="Pressure gauge PG-01" /></div>
            <div><label className="mono-label mb-2 block text-steel">Category</label><select className={inputCls} value={itemForm.category} onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })} data-testid="service-item-category-select">{SERVICES.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}</select></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="mono-label mb-2 block text-steel">Site</label><input className={inputCls} value={itemForm.site_name} onChange={(e) => setItemForm({ ...itemForm, site_name: e.target.value })} data-testid="service-item-site-input" placeholder="Tuas workshop" /></div>
              <div><label className="mono-label mb-2 block text-steel">Country / location</label><input className={inputCls} value={itemForm.country} onChange={(e) => setItemForm({ ...itemForm, country: e.target.value })} data-testid="service-item-country-input" placeholder="Singapore, Germany, Brazil…" /></div>
            </div>
            <div><label className="mono-label mb-2 block text-steel">Contractor</label><input className={inputCls} value={itemForm.contractor_name} onChange={(e) => setItemForm({ ...itemForm, contractor_name: e.target.value })} data-testid="service-item-contractor-input" placeholder="Current contractor" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="mono-label mb-2 block text-steel">Contractor email</label><input type="email" className={inputCls} value={itemForm.contractor_email} onChange={(e) => setItemForm({ ...itemForm, contractor_email: e.target.value })} data-testid="service-item-contractor-email-input" /></div>
              <div><label className="mono-label mb-2 block text-steel">Contractor phone</label><input className={inputCls} value={itemForm.contractor_phone} onChange={(e) => setItemForm({ ...itemForm, contractor_phone: e.target.value })} data-testid="service-item-contractor-phone-input" /></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="mono-label mb-2 block text-steel">Last service</label><input type="date" className={inputCls} value={itemForm.last_service_date} onChange={(e) => setItemForm({ ...itemForm, last_service_date: e.target.value })} data-testid="service-item-last-service-input" /></div>
              <div><label className="mono-label mb-2 block text-steel">Next due</label><input required type="date" className={inputCls} value={itemForm.next_due_date} onChange={(e) => setItemForm({ ...itemForm, next_due_date: e.target.value })} data-testid="service-item-next-due-input" /></div>
            </div>
            <div><label className="mono-label mb-2 block text-steel">Notes</label><textarea rows={3} className={`${inputCls} resize-y`} value={itemForm.notes} onChange={(e) => setItemForm({ ...itemForm, notes: e.target.value })} data-testid="service-item-notes-textarea" /></div>
            <button type="submit" disabled={acting} data-testid="service-item-submit-button" className="flex w-full items-center justify-center gap-2 bg-volt px-5 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink disabled:opacity-50"><Plus size={15} weight="bold" /> Add item</button>
          </form>
        </Panel>

        <div className="lg:col-span-7">
          <Panel title="Register" testId="service-item-list">
            {items.length === 0 ? (
              <p className="text-sm text-steel" data-testid="service-register-empty">No items yet. Add your first calibrated instrument, machine or maintenance contract.</p>
            ) : (
              <div className="space-y-3">
                {items.map((item) => {
                  const days = daysUntil(item.next_due_date);
                  return (
                    <button type="button" key={item.id} onClick={() => setSelectedId(item.id)} data-testid={`service-item-${item.id}`} className={`w-full border p-4 text-left transition-colors duration-200 ${selectedId === item.id ? 'border-volt bg-volt/5' : 'border-line bg-ink hover:border-steel/50'}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-display text-sm font-bold text-parchment">{item.item_name}</p>
                          <p className="mt-1 text-xs text-steel">{item.category} · {item.site_name || item.country_name}</p>
                        </div>
                        <StatusPill status={days !== null && days < 0 ? 'overdue' : item.status} />
                      </div>
                      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-steel/70">Next due {formatDate(item.next_due_date)}{days !== null ? ` · ${days < 0 ? `${Math.abs(days)} days overdue` : days === 0 ? 'due today' : `${days} days`}` : ''}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </Panel>

          {selected && (
            <Panel title={selected.item_name} testId="service-item-detail" className="mt-6">
              <div className="flex flex-wrap items-center gap-3">
                <StatusPill status={selected.status} />
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-steel">{selected.country_name}</span>
                <button type="button" onClick={requestVisit} disabled={acting} data-testid="service-request-visit-button" className="ml-auto flex items-center gap-2 bg-volt px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink disabled:opacity-50"><Wrench size={15} /> Request another visit</button>
              </div>
              <div className="mt-5 grid gap-4 sm:grid-cols-3">
                <div><p className="mono-label text-steel">Contractor</p><p className="mt-1 text-sm text-parchment">{selected.contractor_name || '—'}</p></div>
                <div><p className="mono-label text-steel">Last service</p><p className="mt-1 text-sm text-parchment">{formatDate(selected.last_service_date)}</p></div>
                <div><p className="mono-label text-steel">Next due</p><p className="mt-1 text-sm text-volt">{formatDate(selected.next_due_date)}</p></div>
              </div>
              {selected.notes && <p className="mt-4 whitespace-pre-line border border-line bg-ink p-4 text-sm text-steel">{selected.notes}</p>}

              <div className="mt-8">
                <h3 className="font-display text-lg font-black text-parchment">Record a visit</h3>
                <form onSubmit={createVisit} className="mt-4 space-y-4" data-testid="service-visit-form">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div><label className="mono-label mb-2 block text-steel">Visit date</label><input required type="date" className={inputCls} value={visitForm.visit_date} onChange={(e) => setVisitForm({ ...visitForm, visit_date: e.target.value })} data-testid="service-visit-date-input" /></div>
                    <div><label className="mono-label mb-2 block text-steel">Contractor</label><input className={inputCls} value={visitForm.contractor_name} onChange={(e) => setVisitForm({ ...visitForm, contractor_name: e.target.value })} data-testid="service-visit-contractor-input" placeholder={selected.contractor_name || 'Contractor'} /></div>
                    <div><label className="mono-label mb-2 block text-steel">Next due</label><input type="date" className={inputCls} value={visitForm.next_due_date} onChange={(e) => setVisitForm({ ...visitForm, next_due_date: e.target.value })} data-testid="service-visit-next-due-input" /></div>
                  </div>
                  <div><label className="mono-label mb-2 block text-steel">Work done</label><textarea required rows={3} className={`${inputCls} resize-y`} value={visitForm.work_done} onChange={(e) => setVisitForm({ ...visitForm, work_done: e.target.value })} data-testid="service-visit-work-textarea" /></div>
                  <div><label className="mono-label mb-2 block text-steel">Notes</label><textarea rows={2} className={`${inputCls} resize-y`} value={visitForm.notes} onChange={(e) => setVisitForm({ ...visitForm, notes: e.target.value })} data-testid="service-visit-notes-textarea" /></div>
                  <div>
                    <input id="service-visit-file" type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.zip" onChange={(e) => setVisitFile(e.target.files?.[0] || null)} />
                    <label htmlFor="service-visit-file" data-testid="service-visit-file-dropzone" className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-line px-4 py-4 text-sm text-steel hover:border-volt hover:text-parchment"><Paperclip size={15} /> {visitFile ? visitFile.name : 'Attach report / certificate'}</label>
                  </div>
                  <button type="submit" disabled={acting} data-testid="service-visit-submit-button" className="flex items-center gap-2 bg-volt px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink disabled:opacity-50"><CalendarPlus size={15} /> Record visit</button>
                </form>
              </div>

              <div className="mt-8">
                <h3 className="font-display text-lg font-black text-parchment">Service history</h3>
                {(selected.visits || []).length === 0 ? (
                  <p className="mt-3 text-sm text-steel" data-testid="service-no-visits">No visits recorded yet.</p>
                ) : (
                  <div className="mt-4 space-y-3" data-testid="service-visit-list">
                    {selected.visits.map((visit) => (
                      <div key={visit.id} className="border border-line bg-ink p-4" data-testid={`service-visit-${visit.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <p className="text-sm font-bold text-parchment">{formatDate(visit.visit_date)} · {visit.contractor_name || 'Contractor'}</p>
                          {visit.file?.storage_path && <a href={`${API}/files/service-visit/${visit.id}`} data-testid={`service-visit-file-${visit.id}`} className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-volt"><FileArrowDown size={14} /> {visit.file.original_filename}</a>}
                        </div>
                        <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-steel">{visit.work_done}</p>
                        {visit.next_due_date && <p className="mt-2 text-xs text-volt">Next due: {formatDate(visit.next_due_date)}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Panel>
          )}
        </div>
      </div>
    </DashboardShell>
  );
};

export default ServiceRegister;
